import { DOCUMENT_TYPE_LABEL, clientStatusDisplay, isDocumentType } from './enums';
import type { ApiClient } from './clients';
import type { ApiDocument } from './documents';
import { toNoteView, type ApiNote } from './notes';
import { UNASSIGNED_LABEL, type ApiTask } from './tasks';

/** Searchable entity types. Triage and communications have no real source yet (Task 4.9 adds GET /api/search). */
export type SearchResultType = 'client' | 'task' | 'note' | 'document';

/** The `?section=` value the app navigates to for each result type. */
export type SearchSection = 'client-portal' | 'tasks' | 'notes' | 'documents';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle: string;
  section: SearchSection;
}

/** A term shorter than this (after trimming) is never sent to the API. */
export const MIN_SEARCH_LENGTH = 2;

const NOTE_SNIPPET_LENGTH = 80;
const NO_CLIENT_LABEL = 'No client';

/** Trimmed search term. */
export function normaliseTerm(raw: string): string {
  return raw.trim();
}

/** True when the trimmed term is long enough to search. */
export function isSearchable(raw: string): boolean {
  return normaliseTerm(raw).length >= MIN_SEARCH_LENGTH;
}

/** Case-insensitive match of the (trimmed) term against any of the fields; missing fields never match. */
function matchesTerm(term: string, fields: readonly (string | null | undefined)[]): boolean {
  const needle = normaliseTerm(term).toLowerCase();
  if (!needle) return false;
  return fields.some((field) => typeof field === 'string' && field.toLowerCase().includes(needle));
}

/**
 * Client results. `GET /api/clients?search=` already matched name or email on the server,
 * so every row passed in is a result; nothing is filtered again here.
 */
export function toClientResults(clients: readonly ApiClient[]): SearchResult[] {
  return clients.map((client) => {
    const status = clientStatusDisplay(client.status).label;
    const email = client.email?.trim();
    return {
      id: client.id,
      type: 'client',
      title: client.name,
      subtitle: email ? `${status} • ${email}` : status,
      section: 'client-portal',
    };
  });
}

/** Tasks whose title, description, client name or assignee name contains the term. */
export function searchTasks(tasks: readonly ApiTask[], term: string): SearchResult[] {
  return tasks
    .filter((task) => matchesTerm(term, [task.title, task.description, task.client?.name, task.assignee?.name]))
    .map((task) => ({
      id: task.id,
      type: 'task',
      title: task.title,
      subtitle: `${task.assignee?.name ?? UNASSIGNED_LABEL} • ${task.client?.name ?? NO_CLIENT_LABEL}`,
      section: 'tasks',
    }));
}

function snippet(content: string): string {
  const flat = content.replace(/\s+/g, ' ').trim();
  return flat.length > NOTE_SNIPPET_LENGTH ? `${flat.slice(0, NOTE_SNIPPET_LENGTH)}...` : flat;
}

/** Notes whose title, content or a tag contains the term. */
export function searchNotes(notes: readonly ApiNote[], term: string): SearchResult[] {
  return notes
    .map(toNoteView)
    .filter((note) => matchesTerm(term, [note.title, note.content, ...note.tags]))
    .map((note) => ({
      id: note.id,
      type: 'note',
      title: note.title,
      subtitle: snippet(note.content),
      section: 'notes',
    }));
}

/** Documents whose name, client name or type label contains the term. */
export function searchDocuments(documents: readonly ApiDocument[], term: string): SearchResult[] {
  const typeLabel = (type: string): string => (isDocumentType(type) ? DOCUMENT_TYPE_LABEL[type] : type);
  return documents
    .filter((doc) => matchesTerm(term, [doc.name, doc.client?.name, typeLabel(doc.type)]))
    .map((doc) => ({
      id: doc.id,
      type: 'document',
      title: doc.name,
      subtitle: `${doc.client?.name ?? '—'} • ${typeLabel(doc.type)}`,
      section: 'documents',
    }));
}

/** The `pagination` block the tasks, notes and documents lists return; absent on a malformed response. */
export interface SearchPagination {
  total?: unknown;
}

/**
 * True when the firm holds more rows than were loaded, so a client-side search covered only part of them.
 * Unknown (missing or unusable `pagination.total`) is never reported as partial.
 */
export function isPartialSearch(loaded: number, pagination: SearchPagination | null | undefined): boolean {
  const total = pagination?.total;
  return typeof total === 'number' && Number.isFinite(total) && total > loaded;
}

/**
 * Caveat shown when a client-side search only covered the newest rows of a resource, for example
 * "Searched the newest 200 of 1,234 tasks." Null when everything was searched or the total is unknown.
 */
export function partialSearchNote(
  noun: string,
  loaded: number,
  pagination: SearchPagination | null | undefined,
): string | null {
  if (!isPartialSearch(loaded, pagination)) return null;
  const total = Number(pagination?.total);
  return `Searched the newest ${loaded.toLocaleString('en-AU')} of ${total.toLocaleString('en-AU')} ${noun}.`;
}
